/*!
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import {
  AppStore,
  UnlockWriteAction,
  getCurrentFolder
} from '@alfresco/aca-shared/store';
import { FileUtils } from '@alfresco/adf-core';
import { Injectable, NgZone, RendererFactory2 } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
//import { ContentManagementService } from '../../services/content-management.service';
import { UploadService, FileModel } from '@alfresco/adf-content-services';
import { UploadJobDocumentsFilesAction, UPLOAD_JOBDOCUMENT_FILES_ACTION } from '../actions/jobdocuments.actions';
import { MatDialog } from '@angular/material/dialog';
import { UploaderPlusComponent } from '../components/uploader-plus/uploader-plus.component';

@Injectable()
export class UploadJobdocumentsEffects {
  //private notificationService = inject(NotificationService);

  private readonly fileInput: HTMLInputElement;

  private readonly uploadMenuButtonSelector = 'app-toolbar-menu button[id="app.toolbar.upload"]';

  constructor(
    private store: Store<AppStore>,
    private actions$: Actions,
    private ngZone: NgZone,
    private uploadService: UploadService,
    rendererFactory: RendererFactory2,
    private dialogRef: MatDialog
  ) {
    const renderer = rendererFactory.createRenderer(null, null);

    this.fileInput = renderer.createElement('input') as HTMLInputElement;
    this.fileInput.id = 'app-upload-files';
    this.fileInput.type = 'file';
    this.fileInput.style.display = 'none';
    this.fileInput.setAttribute('multiple', '');
    this.fileInput.addEventListener('change', (event) => this.upload(event));
    renderer.appendChild(document.body, this.fileInput);
  }

  uploadJobdocumentFiles$ = createEffect(
    () =>
      this.actions$.pipe(
        //tap(val => console.log("uploadJobDocumentFiles called", val) ),
        ofType<UploadJobDocumentsFilesAction>(UPLOAD_JOBDOCUMENT_FILES_ACTION),
        map(() => {
          this.registerFocusingElementAfterModalClose(this.fileInput, this.uploadMenuButtonSelector);
          //console.log("uploadJObDocuments fileInput click calling");
          this.fileInput.click();
        })
      ),
    { dispatch: false }
  );

  private upload(event: any): void {
    this.store
      .select(getCurrentFolder)
      .pipe(take(1))
      .subscribe((node) => {
        //console.log("My upload is called", node);
        if (node?.id) {
          const input = event.currentTarget as HTMLInputElement;
          const files = FileUtils.toFileArray(input.files ?? new FileList()).map(
            (file: any) =>
              new FileModel(file, {
                parentId: node.id,
                path: (file.webkitRelativePath || '').replace(/\/[^\/]*$/, ''),
                nodeType: 'cm:content'
              })
          );

          // custom code for Carlisle uploaderPlus
          if(node && node.nodeType == "cm:folder"){
            this.uploadQueue(files);
            event.target.value = '';
          }else{
              //console.log('Files ', files);
              const dialogInstance = this.dialogRef.open(UploaderPlusComponent, {
                data: { node, files },
                width: '600px'
              });

              dialogInstance.afterClosed().subscribe(propArr => {
                //console.log('Document Dialog props returned to upload effect ', propArr);

                if (propArr && propArr.length == files.length) {
                  for (let index in files) {
                    files[index].options.properties = propArr[index];
                  }

                  this.uploadQueue(files);
                }
                event.target.value = '';
              });
            }

          // this.uploadQueue(files);
          // event.target.value = '';
        }
      });
  }

  private uploadQueue(files: FileModel[]) {
    if (files.length > 0) {
      this.ngZone.run(() => {
        this.uploadService.addToQueue(...files);
        this.uploadService.uploadFilesInTheQueue();
      });
    }
  }

  uploadAndUnlock(file: FileModel | null) {
    if (!file) {
      return;
    }

    this.ngZone.run(() => {
      this.uploadService.addToQueue(file);
      this.uploadService.uploadFilesInTheQueue();

      const subscription = this.uploadService.fileUploadComplete.subscribe((completed) => {
        if (file.data?.entry?.properties?.['cm:lockType'] === 'WRITE_LOCK' && file.data?.entry?.id === completed.data.entry.id) {
          this.store.dispatch(new UnlockWriteAction(completed.data));
        }

        subscription.unsubscribe();
      });
    });
  }

  private registerFocusingElementAfterModalClose(input: HTMLInputElement, focusedElementSelector: string): void {
    input.addEventListener(
      'click',
      () => {
        window.addEventListener(
          'focus',
          () => {
            const elementToFocus = document.querySelector<HTMLElement>(focusedElementSelector);
            if(elementToFocus != null){
              elementToFocus.addEventListener('focus', () => elementToFocus.classList.add('cdk-program-focused'), {
                once: true
              });
              elementToFocus.focus();
            }
          },
          {
            once: true
          }
        );
      },
      {
        once: true
      }
    );
  }
}
